import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { STATIC } from '../../lib/queryPresets';
import type { TablesUpdate } from '../../types/database.types';
import {
  getAllTicketCategories,
  createTicketCategory,
  updateTicketCategory,
  reorderTicketCategories,
} from '../../services/collaborators/ticketCategoryService';

const categoriesKey = ['ticket_categories'] as const;

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function useTicketCategories(includeInactive = true) {
  return useQuery({
    ...STATIC,
    queryKey: [...categoriesKey, { includeInactive }],
    queryFn: () => getAllTicketCategories(includeInactive),
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useCreateTicketCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { label: string; description?: string | null; pole?: string | null }) =>
      createTicketCategory(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: categoriesKey }),
  });
}

export function useUpdateTicketCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...input
    }: { id: string } & Pick<
      TablesUpdate<'ticket_categories'>,
      'label' | 'description' | 'is_active' | 'display_order'
    >) => updateTicketCategory(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: categoriesKey }),
  });
}

export function useReorderTicketCategories() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orderedIds: string[]) => reorderTicketCategories(orderedIds),
    onSuccess: () => qc.invalidateQueries({ queryKey: categoriesKey }),
  });
}
